import React, { useState, useEffect } from 'react';
import SelectField from '../common/SelectField.js';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';
import Footer from './Footer';
import { contactService } from '../../services/contactService';
import { useTranslation } from 'react-i18next';


const ContactUs = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    location: '',
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await contactService.createContact(formData);
      setSuccess(true);
      setFormData({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        location: '',
        subject: '',
        message: ''
      });
      
      setTimeout(() => {
        navigate('/');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateBusinessHours = () => {
    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.getHours() * 100 + now.getMinutes();
    
    const businessHours = {
      0: { name: 'Sunday', open: 1000, close: 1600 },
      1: { name: 'Monday', open: 900, close: 1900 },
      2: { name: 'Tuesday', open: 900, close: 1900 },
      3: { name: 'Wednesday', open: 900, close: 1900 },
      4: { name: 'Thursday', open: 900, close: 1900 },
      5: { name: 'Friday', open: 900, close: 1900 },
      6: { name: 'Saturday', open: 900, close: 1700 }
    };
    
    const today = businessHours[currentDay];
    const isOpen = currentTime >= today.open && currentTime <= today.close;
    
    return {
      isOpen,
      currentDay,
      timeString: now.toLocaleString('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Asia/Kolkata'
      })
    };
  };

  const [businessStatus, setBusinessStatus] = useState(updateBusinessHours());

  useEffect(() => {
    const interval = setInterval(() => {
      setBusinessStatus(updateBusinessHours());
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page Header */}
      <section className="bg-gradient-to-br from-blue-600 to-blue-800 text-white py-24 md:py-32 pt-[120px]">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Get In Touch</h1>
          <p className="text-xl md:text-2xl text-blue-100">We're here to help with all your logistics needs</p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-gray-50">
        <div className='container mx-auto'>
            <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-12">
              {/* Contact Form */}
              <div className="bg-white rounded-xl shadow-lg p-8 border-t-4 border-blue-600">
                <h2 className="text-2xl font-bold text-blue-600 mb-6">Send us a Message</h2>

                {success && (
                  <div className="bg-green-50 border-l-4 border-green-500 text-green-700 px-4 py-3 rounded-md mb-6">
                    <div className="flex items-center gap-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <p className="font-medium">Message sent successfully! We'll get back to you soon.</p>
                    </div>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border-l-4 border-red-500 text-red-700 px-4 py-3 rounded-md mb-6">
                    <p className="font-medium">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        First Name <span className="text-red-500 text-sm">*</span>
                      </label>
                      <input
                        type="text"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        placeholder="Enter your first name"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        Last Name <span className="text-red-500 text-sm">*</span>
                      </label>
                      <input
                        type="text"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        placeholder="Enter your last name"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        Email Address <span className="text-red-500 text-sm">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email address"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        Phone Number <span className="text-red-500 text-sm">*</span>
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="Enter your phone number"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        Location
                      </label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        placeholder="Enter your location (optional)"
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-md font-semibold text-gray-700 mb-2">
                        Subject <span className="text-red-500 text-sm">*</span>
                      </label>
                      <SelectField variant="form"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none"
                        required
                      >
                        <option value="">Select a subject</option>
                        <option value="general">General Inquiry</option>
                        <option value="quote">Request Quote</option>
                        <option value="support">Customer Support</option>
                        <option value="partnership">Partnership</option>
                        <option value="complaint">Complaint</option>
                      </SelectField>
                    </div>
                  </div>
                  <div>
                    <label className="block text-md font-semibold text-gray-700 mb-2">
                      Message<span className="text-red-500 text-sm">*</span>
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Enter your message"
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-600 focus:outline-none resize-none"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-auto bg-blue-600 text-white py-3 px-8 rounded-lg hover:bg-blue-700 font-medium transition-all duration-300 disabled:opacity-50 hover:shadow-lg"
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                  </button>
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                {/* Business Hours */}
                <div className="bg-white rounded-xl shadow-md p-6 border-t-4 border-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    Business Hours
                  </h3>
                  
                  <div className={`mb-4 p-3 rounded-lg ${businessStatus.isOpen ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Current Status:</span>
                      <span className={`font-bold ${businessStatus.isOpen ? 'text-green-600' : 'text-red-600'}`}>
                        {businessStatus.isOpen ? 'OPEN' : 'CLOSED'}
                      </span>
                    </div>
                    <div className="text-sm text-gray-600 mt-1">{businessStatus.timeString}</div>
                  </div>
                  
                  <div className="space-y-4 p-2">
                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'].map((day, idx) => (
                      <div key={day} className={`flex justify-between ${businessStatus.currentDay === idx + 1 ? 'text-orange-500 font-bold' : ''}`}>
                        <span className="text-gray-700">{day}:</span>
                        <span className="text-gray-900 font-medium">9:00 AM - 7:00 PM</span>
                      </div>
                    ))}
                    <div className={`flex justify-between ${businessStatus.currentDay === 6 ? 'text-orange-500 font-bold' : ''}`}>
                      <span className="text-gray-700">Saturday:</span>
                      <span className="text-gray-900 font-medium">9:00 AM - 5:00 PM</span>
                    </div>
                    <div className={`flex justify-between ${businessStatus.currentDay === 0 ? 'text-orange-500 font-bold' : ''}`}>
                      <span className="text-gray-700">Sunday:</span>
                      <span className="text-gray-900 font-medium">10:00 AM - 4:00 PM</span>
                    </div>
                  </div>
                </div>

                {/* Emergency Contact */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 border-t-4 border-t-blue-600">
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                    <svg className="w-6 h-6 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    Emergency Support
                  </h3>
                  <p className="text-gray-700 mb-2">For urgent shipment issues or emergencies:</p>
                  <div className="flex items-center">
                    <svg className="w-5 h-5 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span className="text-gray-900 font-medium">+91 94805 01861</span>
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Available 24/7 for emergency support</p>
                </div>
              </div>
            </div>
          </div>

          {/* Locations Section */}
          <div className="my-0 py-16 md:pt-24 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-12">
                <p className="text-blue-600 font-semibold text-sm uppercase tracking-widest mb-3">CONTACT</p>
                <h2 className="text-4xl font-bold text-gray-900 mt-4 max-w-[700px] mx-auto">
                  Visit Our Head Office
                </h2>
              </div>
              <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm'>
                  <div className="w-full lg:col-span-3 flex justify-center">
                    {/* Head Office Card */}
                    <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-xl transition-all border-t-4 border-blue-600 relative overflow-hidden max-w-2xl w-full">
                      <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full opacity-10"></div>

                      <div className="relative z-10">
                        {/* Header */}
                        <div className="flex items-center mb-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-4">
                            <svg
                              className="w-6 h-6 text-blue-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a2 2 0 012-2h2a2 2 0 012 2v5m-4 0h4"
                              />
                            </svg>
                          </div>

                          <h3 className="text-md font-bold text-gray-800">
                            Office Address :
                          </h3>

                          <span className="ml-auto bg-blue-100 text-blue-800 text-sm font-semibold px-3 py-1 rounded-full">
                            Head Office
                          </span>
                        </div>

                        {/* Content */}
                        <div className="space-y-4">
                          {/* Address */}
                          <div className="flex items-start">
                            <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <div> 
                              <p className="font-medium text-sm">Plot No 13, Shivaji Co-Operative Housing Society LTD,</p>
                              <p className="font-medium text-sm">Crystal Compound, Sion Trombay Road,</p>
                              <p className="font-medium text-sm"> Mumbai.</p>
                            </div>
                          </div>

                          {/* Phone */}
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                            <span className="font-medium">+91 94805 01861</span>
                          </div>

                          {/* Email */}
                          <div className="flex items-center">
                            <svg className="w-5 h-5 text-blue-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                            <span>admin@metrologistics.co.in</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>

            </div>
          </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactUs;
